/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ICommandBarItemProps, ICommandBarProps, ICommandBarStyles } from '@fluentui/react/lib/CommandBar';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logCommandBarButtons } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { TelemetryFeatureTitle } from 'src/core/types/telemetryTypes';

export type DemoCommandBarProps = {
    getSdkLink?: string;
    featureName?: TelemetryFeatureTitle;
    documentationLink?: string;
    gitHubSamplesLink?: string;
};

const messages = defineMessages({
    getSdk: { id: 'languageStudio.demoCommandBar.getSdk', defaultMessage: 'Get SDK' },
    viewDocs: { id: 'languageStudio.demoCommandBar.viewDocs', defaultMessage: 'View documentation' },
    viewGitHubSamples: { id: 'languageStudio.demoCommandBar.viewGitHubSamples', defaultMessage: 'View samples on GitHub' }
});

const commandBarTheme = getScopedTheme('DemoCommandBar');
const commandBarStyles = classNamesFunction<ICommandBarProps, ICommandBarStyles>()({
    root: { borderBottom: `1px solid ${commandBarTheme.topBorderColor}`, borderTop: 'none !important' }
});

const navigateTo = (url: string, itemName: string, featureName: TelemetryFeatureTitle) => {
    logCommandBarButtons(featureName, itemName);
    window.open(url, '_blank');
};

/**
 * Displays the available commands to the current demo.
 */
export const DemoCommandBar = (props: DemoCommandBarProps) => {
    const { getSdkLink, gitHubSamplesLink, documentationLink, featureName } = props;
    const { localize } = useLocalization();

    const items: ICommandBarItemProps[] = [];

    if (documentationLink) {
        items.push({
            key: 'viewDocs',
            text: localize(messages.viewDocs),
            iconProps: { iconName: 'TextDocument' },
            onClick: () => navigateTo(documentationLink, 'viewDocs', featureName)
        });
    }

    if (gitHubSamplesLink) {
        items.push({
            key: 'viewGitHubSamples',
            iconProps: { iconName: 'Github' },
            text: localize(messages.viewGitHubSamples),
            onClick: () => navigateTo(gitHubSamplesLink, 'viewGitHubSamples', featureName)
        });
    }

    if (getSdkLink) {
        items.push({
            key: 'getSdk',
            text: localize(messages.getSdk),
            iconProps: { iconName: 'Code' },
            onClick: () => navigateTo(getSdkLink, 'getSdk', featureName)
        });
    }

    return <LuisCommandBar items={items} styles={commandBarStyles} />;
};
